import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { priorityDashboardAPI, PriorityDashboard as PriorityDashboardData, PriorityItem } from '../../services/api';
import './PriorityDashboard.css';

interface PriorityDashboardProps {
  onNavigateToFinding?: (findingId: number) => void;
}

const PriorityDashboard: React.FC<PriorityDashboardProps> = ({ onNavigateToFinding }) => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState<PriorityDashboardData | null>(null);
  const [topPriorities, setTopPriorities] = useState<PriorityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
  const [priorityLimit, setPriorityLimit] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTopPriorities();
  }, [selectedSeverity, priorityLimit]);

  // Transform backend PriorityScore objects to frontend PriorityItem format
  const transformPriorities = (rawData: any): PriorityItem[] => {
    if (!rawData) return [];

    // Handle both direct array and { priorities: [...] } response
    const priorities = Array.isArray(rawData) ? rawData : (rawData.priorities || []);

    if (!Array.isArray(priorities)) return [];

    return priorities.map((item: any) => {
      // Backend returns PriorityScore with nested Result
      const result = item.Result || item.result || item;
      return {
        id: result.id || item.result_id || item.id,
        title: result.title || result.Title || item.title || 'Unknown',
        severity: result.severity || result.Severity || item.score_tier || 'medium',
        priority_score: Math.round(item.score || item.Score || item.priority_score || 0),
        host: result.host || result.Host || result.ip || result.IP || 'Unknown',
        cve_id: result.cve_id || result.CVEID || undefined,
        fix_complexity: item.fix_complexity || 'medium',
        business_impact: item.business_impact || item.score_tier || 'medium',
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardRes, prioritiesRes] = await Promise.all([
        priorityDashboardAPI.getDashboard(),
        priorityDashboardAPI.getTopPriorities({ limit: priorityLimit }),
      ]);
      setDashboard(dashboardRes.data);
      setTopPriorities(transformPriorities(prioritiesRes.data));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch priority data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopPriorities = async () => {
    try {
      const response = await priorityDashboardAPI.getTopPriorities({
        limit: priorityLimit,
        severity: selectedSeverity || undefined,
      });
      setTopPriorities(transformPriorities(response.data));
    } catch (err) {
      console.error('Failed to fetch top priorities:', err);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'var(--color-critical)';
      case 'high': return 'var(--color-warning-dark)';
      case 'medium': return 'var(--color-warning-dark)';
      case 'low': return 'var(--color-primary-600)';
      default: return 'var(--color-text-tertiary)';
    }
  };

  const getComplexityIcon = (complexity: string): string => {
    switch (complexity) {
      case 'easy': return 'fa-check-circle';
      case 'medium': return 'fa-minus-circle';
      case 'hard': return 'fa-times-circle';
      default: return 'fa-question-circle';
    }
  };

  const getComplexityColor = (complexity: string): string => {
    switch (complexity) {
      case 'easy': return 'var(--color-success)';
      case 'medium': return 'var(--color-warning)';
      case 'hard': return 'var(--color-error)';
      default: return 'var(--color-text-tertiary)';
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'critical': return 'var(--color-critical)';
      case 'high': return 'var(--color-warning-dark)';
      case 'medium': return 'var(--color-warning-dark)';
      case 'low': return 'var(--color-success)';
      default: return 'var(--color-text-tertiary)';
    }
  };

  if (loading) {
    return (
      <div className="priority-dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Loading priority dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="priority-dashboard-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>{error}</span>
        <button onClick={fetchData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="priority-dashboard">
      <div className="priority-header">
        <div className="header-title">
          <i className="fas fa-tasks"></i>
          <h2>What to Fix First</h2>
        </div>
        <p className="header-subtitle">
          Prioritized vulnerabilities based on risk, exploitability, and business impact
        </p>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="priority-summary">
          <div className="summary-card total">
            <div className="card-icon">
              <i className="fas fa-bug"></i>
            </div>
            <div className="card-content">
              <span className="card-value">{dashboard.total_findings}</span>
              <span className="card-label">Total Findings</span>
            </div>
          </div>
          <div className="summary-card critical" onClick={() => setSelectedSeverity(selectedSeverity === 'critical' ? null : 'critical')}>
            <div className="card-icon">
              <i className="fas fa-skull-crossbones"></i>
            </div>
            <div className="card-content">
              <span className="card-value">{dashboard.critical_count}</span>
              <span className="card-label">Critical</span>
            </div>
            {selectedSeverity === 'critical' && <i className="fas fa-check filter-active"></i>}
          </div>
          <div className="summary-card high" onClick={() => setSelectedSeverity(selectedSeverity === 'high' ? null : 'high')}>
            <div className="card-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="card-content">
              <span className="card-value">{dashboard.high_count}</span>
              <span className="card-label">High</span>
            </div>
            {selectedSeverity === 'high' && <i className="fas fa-check filter-active"></i>}
          </div>
          <div className="summary-card medium" onClick={() => setSelectedSeverity(selectedSeverity === 'medium' ? null : 'medium')}>
            <div className="card-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="card-content">
              <span className="card-value">{dashboard.medium_count}</span>
              <span className="card-label">Medium</span>
            </div>
            {selectedSeverity === 'medium' && <i className="fas fa-check filter-active"></i>}
          </div>
          <div className="summary-card low" onClick={() => setSelectedSeverity(selectedSeverity === 'low' ? null : 'low')}>
            <div className="card-icon">
              <i className="fas fa-info-circle"></i>
            </div>
            <div className="card-content">
              <span className="card-value">{dashboard.low_count}</span>
              <span className="card-label">Low</span>
            </div>
            {selectedSeverity === 'low' && <i className="fas fa-check filter-active"></i>}
          </div>
        </div>
      )}

      {/* Priority List */}
      <div className="priority-list-section">
        <div className="list-header">
          <h3>
            <i className="fas fa-sort-amount-down"></i>
            Top Priorities
            {selectedSeverity && <span className="filter-badge">{selectedSeverity}</span>}
          </h3>
          <div className="list-controls">
            <select
              value={priorityLimit}
              onChange={(e) => setPriorityLimit(Number(e.target.value))}
              className="limit-select"
            >
              <option value={5}>Top 5</option>
              <option value={10}>Top 10</option>
              <option value={25}>Top 25</option>
              <option value={50}>Top 50</option>
            </select>
            {selectedSeverity && (
              <button className="clear-filter" onClick={() => setSelectedSeverity(null)}>
                <i className="fas fa-times"></i>
                Clear Filter
              </button>
            )}
          </div>
        </div>

        <div className="priority-list">
          {topPriorities.length === 0 ? (
            <div className="no-priorities">
              <i className="fas fa-check-circle"></i>
              <p>No vulnerabilities found matching the criteria</p>
            </div>
          ) : (
            topPriorities.map((item, index) => (
              <div
                key={item.id}
                className="priority-item"
                onClick={() => onNavigateToFinding?.(item.id)}
              >
                <div className="priority-rank">
                  <span className="rank-number">#{index + 1}</span>
                  <div
                    className="priority-score-bar"
                    style={{
                      background: `linear-gradient(90deg, ${getSeverityColor(item.severity)} ${item.priority_score}%, transparent ${item.priority_score}%)`
                    }}
                  ></div>
                </div>

                <div className="priority-content">
                  <div className="priority-header-row">
                    <h4 className="priority-title">{item.title}</h4>
                    <span
                      className="priority-score"
                      style={{ color: getSeverityColor(item.severity) }}
                    >
                      {item.priority_score}
                    </span>
                  </div>

                  <div className="priority-meta">
                    <span className="meta-item host">
                      <i className="fas fa-server"></i>
                      {item.host}
                    </span>
                    {item.cve_id && (
                      <span className="meta-item cve">
                        <i className="fas fa-shield-alt"></i>
                        {item.cve_id}
                      </span>
                    )}
                  </div>

                  <div className="priority-badges">
                    <span
                      className="badge severity"
                      style={{ backgroundColor: getSeverityColor(item.severity) }}
                    >
                      {item.severity}
                    </span>
                    <span
                      className="badge complexity"
                      style={{ borderColor: getComplexityColor(item.fix_complexity) }}
                    >
                      <i
                        className={`fas ${getComplexityIcon(item.fix_complexity)}`}
                        style={{ color: getComplexityColor(item.fix_complexity) }}
                      ></i>
                      {item.fix_complexity} fix
                    </span>
                    <span
                      className="badge impact"
                      style={{ borderColor: getImpactColor(item.business_impact) }}
                    >
                      <i
                        className="fas fa-building"
                        style={{ color: getImpactColor(item.business_impact) }}
                      ></i>
                      {item.business_impact} impact
                    </span>
                  </div>
                </div>

                <div className="priority-action">
                  <i className="fas fa-chevron-right"></i>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Risk Trend Chart Placeholder */}
      {dashboard?.risk_trend && dashboard.risk_trend.length > 0 && (
        <div className="risk-trend-section">
          <h3>
            <i className="fas fa-chart-line"></i>
            Risk Trend
          </h3>
          <div className="trend-chart">
            <div className="trend-bars">
              {dashboard.risk_trend.slice(-14).map((point, idx) => (
                <div key={idx} className="trend-bar-container">
                  <div
                    className="trend-bar"
                    style={{
                      height: `${point.score}%`,
                      backgroundColor: point.score > 70 ? 'var(--color-error)' :
                                       point.score > 40 ? 'var(--color-warning)' : 'var(--color-success)'
                    }}
                    title={`${point.date}: ${point.score}`}
                  ></div>
                  <span className="trend-date">
                    {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriorityDashboard;
